import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { findCaseStudy, CASE_STUDIES } from "@/data/content";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const c = findCaseStudy(slug);
  if (!c) return <Navigate to="/case-studies" replace />;

  return (
    <>
      <SEO title={c.title} description={c.summary} />

      <section className="bg-[var(--dm-navy)] text-white">
        <div className="dm-container py-24 lg:py-32">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"><ArrowLeft className="w-4 h-4" /> All engagement examples</Link>
          <div className="dm-overline text-[var(--dm-emerald)] mt-8">{c.industry}</div>
          <h1 className="font-serif text-5xl sm:text-6xl mt-4 leading-[1.05] max-w-4xl">{c.title}</h1>
          <p className="mt-6 text-white/70 max-w-2xl">{c.summary}</p>
          <p className="mt-8 text-xs uppercase tracking-[0.2em] text-white/50">Anonymised — published with client approval</p>
        </div>
      </section>

      <section className="bg-white">
        <div className="aspect-[21/9] overflow-hidden max-h-[520px]">
          <img src={c.image} alt={c.title} className="w-full h-full object-cover" />
        </div>
      </section>

      <section className="bg-white">
        <div className="dm-container dm-section grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-8">
            <div className="dm-overline">The challenge</div>
            <p className="font-serif text-3xl text-[var(--dm-navy)] mt-4 leading-snug">{c.challenge}</p>

            <div className="mt-14">
              <div className="dm-overline">Our approach</div>
              <ul className="mt-6 space-y-3">
                {c.approach.map((a) => (
                  <li key={a} className="flex gap-3 text-[var(--dm-navy)]">
                    <Check className="w-5 h-5 mt-1 text-[var(--dm-emerald)] shrink-0" />
                    <span className="leading-relaxed">{a}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-14">
              <div className="dm-overline">Outcomes</div>
              <div className="grid sm:grid-cols-3 gap-4 mt-6">
                {c.outcome.map((o) => (
                  <div key={o} className="border border-[var(--dm-border)] p-5">
                    <div className="text-sm text-[var(--dm-navy)] leading-relaxed">{o}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 bg-[var(--dm-surface)] p-8 border border-[var(--dm-border)]">
              <div className="dm-overline">More like this</div>
              <ul className="mt-4 space-y-4">
                {CASE_STUDIES.filter((x) => x.slug !== c.slug).slice(0, 3).map((x) => (
                  <li key={x.slug}>
                    <Link to={`/case-studies/${x.slug}`} className="block group">
                      <div className="text-sm font-serif text-[var(--dm-navy)] group-hover:text-[var(--dm-emerald)]">{x.title}</div>
                      <div className="text-xs text-[var(--dm-muted)] mt-1">{x.industry}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <CTASection />
    </>
  );
}
